import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import {
  getNotificationPreferences,
  updateNotificationPreferences
} from '../services/api/implementation/impl';

/**
 * Per-event notification settings.
 *
 * The event list comes from the backend rather than being hard-coded here, so
 * adding an event to the rules registry surfaces it on this screen with no
 * frontend change — and, more importantly, this screen can never drift into
 * offering a toggle for an event that no longer exists.
 *
 * Storage is sparse: the API keeps a row only where something is switched off.
 * So the UI's model is "on unless an override says otherwise", and switching
 * something back on deletes the row rather than storing a redundant yes.
 */

const CHANNELS = [
  { key: 'inapp', label: 'In-app', hint: 'Shows in the bell and the notifications page' },
  { key: 'push', label: 'Push', hint: 'Sends to your phone and browser' }
];

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { collapsed } = useSidebar();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: getNotificationPreferences,
    retry: 1
  });

  const events = useMemo(() => data?.data?.events || [], [data]);
  const overrides = useMemo(() => data?.data?.overrides || [], [data]);

  // Local mirror so toggles feel immediate; reconciled from the server on load
  // and after every save.
  const [disabled, setDisabled] = useState(() => new Set());

  useEffect(() => {
    const next = new Set();
    for (const row of overrides) {
      if (row.enabled === false) next.add(`${row.event_type}|${row.channel}`);
    }
    setDisabled(next);
  }, [overrides]);

  const save = useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Notification settings saved');
    },
    onError: () => {
      toast.error('Could not save notification settings');
      // The local mirror is now ahead of the server; pull it back into line.
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    }
  });

  const isOn = (event, channel) => !disabled.has(`${event}|${channel}`);

  const toggle = (event, channel) => {
    const key = `${event}|${channel}`;
    const next = new Set(disabled);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setDisabled(next);

    // Send the full picture rather than a delta: the endpoint replaces the
    // user's overrides, and a delta would leave anything omitted ambiguous.
    const payload = [];
    for (const e of events) {
      for (const c of CHANNELS) {
        payload.push({
          event_type: e.event,
          channel: c.key,
          enabled: !next.has(`${e.event}|${c.key}`)
        });
      }
    }
    save.mutate(payload);
  };

  // Grouped for reading, not for storage — the API is flat.
  const byVertical = useMemo(() => {
    const groups = new Map();
    for (const e of events) {
      if (!groups.has(e.vertical)) groups.set(e.vertical, []);
      groups.get(e.vertical).push(e);
    }
    return [...groups.entries()];
  }, [events]);

  return (
    <Layout>
      <div className="w-full h-full">
        <div
          className={`fixed top-0 z-10 transition-all duration-300 bg-white ${
            collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
          }`}>
          <Header data={{ name: 'Notification settings' }} backArrow={true} />
        </div>

        <GridWrapper width={100}>
          <div className="mt-[6rem] mx-auto max-w-3xl pb-10">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Turn off anything you would rather not be told about. Everything is on
                unless you switch it off.
              </p>
              <button
                type="button"
                onClick={() => navigate('/builder/notifications')}
                className="shrink-0 text-sm font-medium text-blue-600 hover:text-blue-700">
                Back to notifications
              </button>
            </div>

            {isLoading && (
              <p className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                Loading…
              </p>
            )}

            {isError && !isLoading && (
              <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center">
                <p className="text-sm font-medium text-gray-800">
                  Settings could not be loaded
                </p>
                <p className="mt-1 text-sm text-gray-500">Refresh the page to try again.</p>
              </div>
            )}

            {!isLoading && !isError && byVertical.length === 0 && (
              <p className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                No notification types are configured.
              </p>
            )}

            {byVertical.map(([vertical, verticalEvents]) => (
              <section key={vertical} className="mb-5">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {vertical}
                </h2>
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  {verticalEvents.map((e) => (
                    <div
                      key={e.event}
                      className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3.5 last:border-b-0">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{e.title}</p>
                        <p className="mt-0.5 font-mono text-[11px] text-gray-400">
                          {e.event}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-4">
                        {CHANNELS.map((c) => (
                          <label
                            key={c.key}
                            title={c.hint}
                            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={isOn(e.event, c.key)}
                              onChange={() => toggle(e.event, c.key)}
                              disabled={save.isPending}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            {c.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </GridWrapper>
      </div>
    </Layout>
  );
};

export default NotificationPreferences;
